'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ContactForm from './ContactForm';
import BusinessHours from './BusinessHours';

interface BusinessHour {
  day: string;
  hours: string;
  isToday?: boolean;
}

const ContactPageInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const businessHours: BusinessHour[] = [
    { day: 'Isnin - Jumaat', hours: '9:00 AM - 6:00 PM', isToday: true },
    { day: 'Sabtu', hours: '9:00 AM - 2:00 PM' },
    { day: 'Ahad & Cuti Umum', hours: 'Tutup' },
  ];

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="animate-pulse">
          <div className="h-64 bg-muted" />
          <div className="max-w-[1200px] mx-auto px-6 py-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="h-64 bg-muted rounded-xl" />
              <div className="h-64 bg-muted rounded-xl" />
              <div className="h-64 bg-muted rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="flex-1">
      <section className="bg-card border-b border-border">
        <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-24 text-center">
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3 tracking-tight">
            Hubungi Surilipat
          </h1>
          <h2 className="text-xl md:text-2xl font-heading font-semibold mb-4">
            Servis Lipat & Gosok Baju Di Kawasan Anda
          </h2>
          <p className="text-lg text-textSecondary max-w-2xl mx-auto leading-relaxed">
            Ada soalan tentang servis lipat atau gosok baju? Team Surilipat sedia membantu dan menghubungkan anda dengan ejen Surilipat berdaftar yang berdekatan dengan kawasan anda. Pilih cara komunikasi yang paling selesa untuk urusan harian anda.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-card rounded-[2rem] p-8 md:p-12 border border-border shadow-soft relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 text-whatsapp/10 group-hover:scale-110 transition-transform duration-500">
                <svg className="w-32 h-32 fill-current" viewBox="0 0 24 24">
                  <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766 0-3.18-2.587-5.771-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793 0-.852.448-1.271.607-1.445.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86.174.088.275.073.376-.043.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564c.173.087.289.129.332.202.043.073.043.423-.101.827z" />
                </svg>
              </div>
              <div className="relative z-10">
                <h3 className="text-3xl font-heading font-bold mb-4">Sembang di WhatsApp</h3>
                <p className="text-textSecondary text-lg max-w-md">
                  Cara terpantas untuk mendapatkan jawapan dan semak ejen terdekat di kawasan anda.
                </p>
                <p className="text-textSecondary text-lg mb-8 max-w-md">
                  Team support kami akan membantu anda memahami servis, anggaran harga dan proses tempahan sebelum disambungkan kepada ejen yang sesuai.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <a
                    href="https://wa.me/601173285813?text=Salam,%20saya%20ingin%20bertanya%20tentang%20servis%20Surilipat.hub"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto bg-whatsapp text-white px-10 py-5 rounded-2xl font-cta font-bold text-lg shadow-xl shadow-whatsapp/20 flex items-center justify-center gap-3 hover:-translate-y-1 transition-all"
                  >
                    <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
                    <span>Mula Sembang</span>
                  </a>
                  <div className="flex items-center gap-2 text-sm font-medium text-textSecondary">
                    <Icon name="ClockIcon" size={16} variant="outline" />
                    <span>Respons tipikal: &lt; 15 minit</span>
                  </div>
                </div>
              </div>
            </div>

            <ContactForm />
          </div>

          <aside className="space-y-8">
            <BusinessHours
              hours={businessHours}
              responseTime="Kami akan membalas dalam masa maksimum 24 jam pada hari bekerja. Untuk pertanyaan segera, WhatsApp adalah pilihan terbaik."
              emailContact={{
                address: 'support@surilipat.com',
                note: 'Untuk pertanyaan korporat, kerjasama atau urusan rasmi.',
              }}
            />

            <div className="bg-primary/5 p-8 rounded-[2rem] border border-primary/10">
              <h5 className="font-heading font-bold text-lg mb-4 text-primary">Sertai Komuniti</h5>
              <p className="text-sm text-textSecondary leading-relaxed mb-6">
                Ingin menjana pendapatan dari rumah? Kami sentiasa mencari ejen lipat & gosok baju
                yang komited.
              </p>
              <a
                href="/agent-registration"
                className="inline-flex items-center gap-2 text-sm font-cta font-bold text-primary group"
              >
                Jadi Ejen Surilipat
                <Icon
                  name="ArrowRightIcon"
                  size={16}
                  variant="outline"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default ContactPageInteractive;
